import { useEffect, useRef, useState } from 'react';
import type { RefObject } from 'react';

/**
 * Reads the user's motion preference. Returns true when the OS is set to
 * "reduce motion", in which case animations should render their final frame.
 */
export function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return false;
  }
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

export interface UseInViewOptions {
  /** Fraction of the element that must be visible before it counts as in view. */
  threshold?: number;
}

export interface UseInViewResult<T extends HTMLElement> {
  /** Attach to the element you want to observe. */
  ref: RefObject<T>;
  /** True only while the element is currently intersecting. */
  inView: boolean;
  /** Latches true permanently after the first intersection (play-once). */
  seen: boolean;
}

/**
 * Observes an element and reports whether it has entered the viewport.
 * `seen` latches true after the first entry so feature animations play
 * exactly once on scroll instead of looping on every re-entry.
 */
export function useInView<T extends HTMLElement = HTMLDivElement>(
  options: UseInViewOptions = {},
): UseInViewResult<T> {
  const { threshold = 0.15 } = options;
  const ref = useRef<T>(null);
  const [inView, setInView] = useState(false);
  const [seen, setSeen] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;

    if (typeof IntersectionObserver === 'undefined') {
      setInView(true);
      setSeen(true);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setInView(true);
            setSeen(true);
          } else {
            setInView(false);
          }
        });
      },
      { threshold },
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [threshold]);

  return { ref, inView, seen };
}
